/**
 * Tours API Client — per-user completion of feature tours.
 *
 * API Endpoints:
 * - GET /tours → published tours with the user's web/mobile completion
 * - POST /tours/{slug}/complete { platform } → idempotent; first completion wins
 */

import type { TourStatus } from '@/types';
import { apiClient } from './base-client';

// BE `sendResponse` spreads the payload at the top level (`{ message, tours }`),
// although api-tours.md shows a `{ success, data }` envelope — accept both.
type Raw<T> = (T & { data?: T }) | null | undefined;

function payloadOf<T extends object>(res: Raw<T>): Partial<T> {
  if (!res || typeof res !== 'object') return {};
  return res.data && typeof res.data === 'object' ? res.data : res;
}

export const toursClient = {
  list: async (): Promise<TourStatus[]> => {
    const res = await apiClient.get<Raw<{ tours?: TourStatus[] }>>('/tours');
    const { tours } = payloadOf(res);
    return Array.isArray(tours) ? tours : [];
  },

  complete: async (slug: string): Promise<TourStatus | null> => {
    const res = await apiClient.post<Raw<{ tour?: TourStatus }>>(
      `/tours/${encodeURIComponent(slug)}/complete`,
      { platform: 'web' }
    );
    return payloadOf(res).tour ?? null;
  },
};
